import React, { useState, useEffect, useCallback, useMemo, useRef, useContext } from 'react';
import styles from './Notifications.module.css';
import axios from 'axios';
import { useAuthUser } from '../../hooks/useAuthUser';
import { Link } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '@clerk/clerk-react';
import {
	MdNotifications, MdFavorite, MdChatBubble, MdPersonAdd,
	MdFitnessCenter, MdLocalFireDepartment, MdEmojiEvents,
	MdMail, MdCheckCircle, MdDoneAll, MdNotificationsActive
} from 'react-icons/md';
import { AppAuthContext } from '../../contexts/AppAuthContext';
import { buildApiBases, encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import { resolveSocketAuthPayload } from '../../utils/socketAuth';
import formatDisplayName from '../../utils/formatDisplayName';

const SHOW_NOTIFICATION_SETTINGS = false;
const WEB_PUSH_PUBLIC_KEY = (process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '').trim();
const WEB_PUSH_SW_SCOPE = '/web-push/';
const WEB_PUSH_SW_URL = '/web-push-sw.js';

const base64UrlToUint8Array = (base64String) => {
	const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
	const rawData = window.atob(base64);
	const outputArray = new Uint8Array(rawData.length);
	for (let i = 0; i < rawData.length; ++i) {
		outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
};

const waitForServiceWorkerRegistration = async (timeoutMs = 8000) => {
	if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
		return null;
	}

	try {
		const existing = await navigator.serviceWorker.getRegistration(WEB_PUSH_SW_SCOPE);
		if (existing) return existing;
	} catch (_) {
		// continue to register fallback
	}

	try {
		const registration = await navigator.serviceWorker.register(WEB_PUSH_SW_URL, { scope: WEB_PUSH_SW_SCOPE });
		if (registration) return registration;
	} catch (_) {
		// continue to ready() fallback
	}

	try {
		return await Promise.race([
			navigator.serviceWorker.ready,
			new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs))
		]);
	} catch (_) {
		return null;
	}
};

const Notifications = () => {
	const { user } = useAuthUser();
	const { getToken } = useAuth();
	const { testAuthSession } = useContext(AppAuthContext);
	const API_URL = process.env.REACT_APP_API_URL;
	const socketRef = useRef(null);
	const requestApi = useCallback((requestFactory) => requestWithApiFallback(API_URL, requestFactory), [API_URL]);
	const socketBaseUrl = useMemo(() => {
		const bases = buildApiBases(API_URL);
		return bases.find(base => !/\/api$/i.test(base)) || bases[0] || '';
	}, [API_URL]);

	const [notifications, setNotifications] = useState(() => {
		try {
			const cached = sessionStorage.getItem('calcium_notifications_cache');
			if (cached) return JSON.parse(cached);
		} catch { /* ignore */ }
		return [];
	});
	const [pendingInvites, setPendingInvites] = useState([]);
	const [preferences, setPreferences] = useState({
		pushNotifications: true,
		emailNotifications: true,
		scheduleReminders: true,
		workoutReminders: true
	});
	const [reminderSettings, setReminderSettings] = useState({
		timezone: 'UTC',
		defaultWorkoutReminderMinutesBefore: 30
	});
	const [pushTokenInput, setPushTokenInput] = useState('');
	const [loading, setLoading] = useState(true);
	const [pushEnableBusy, setPushEnableBusy] = useState(false);
	const [hasWebPushSubscription, setHasWebPushSubscription] = useState(false);
	const [browserPermission, setBrowserPermission] = useState(
		typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
	);
	const canEnableWebPush = Boolean(
		user?.id &&
		WEB_PUSH_PUBLIC_KEY &&
		typeof window !== 'undefined' &&
		'serviceWorker' in navigator &&
		'PushManager' in window
	);

	const syncBrowserPushState = useCallback(async () => {
		if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
			setHasWebPushSubscription(false);
			return null;
		}
		try {
			const registration = await waitForServiceWorkerRegistration();
			if (!registration) {
				setHasWebPushSubscription(false);
				return null;
			}
			const subscription = await registration.pushManager.getSubscription();
			setHasWebPushSubscription(Boolean(subscription));
			return subscription;
		} catch (_) {
			setHasWebPushSubscription(false);
			return null;
		}
	}, []);

	const fetchNotifications = useCallback(async () => {
		if (!user?.id) return;
		try {
			const res = await requestApi((base) => axios.get(
				`${base}/notifications/${encodePathSegment(user.id)}`
			));
			const data = res.data.notifications || [];
			setNotifications(data);
			try { sessionStorage.setItem('calcium_notifications_cache', JSON.stringify(data)); } catch { /* ignore */ }
		} catch (err) {
			console.error('Fetch notifications error:', err.message);
		} finally {
			setLoading(false);
		}
	}, [requestApi, user?.id]);

	const fetchPendingInvites = useCallback(async () => {
		if (!user?.id) return;
		try {
			const res = await axios.get(`${API_URL}/invites/${user.id}?status=pending`);
			setPendingInvites(Array.isArray(res.data) ? res.data : []);
		} catch (err) {
			console.error('Fetch invites error:', err.message);
		}
	}, [API_URL, user?.id]);

	const fetchPreferences = useCallback(async () => {
		if (!user?.id) return;
		try {
			const res = await axios.get(`${API_URL}/users/${user.id}/preferences`);
			setPreferences(prev => ({ ...prev, ...(res.data.preferences || {}) }));
			setReminderSettings(prev => ({ ...prev, ...(res.data.reminderSettings || {}) }));
		} catch (err) {
			console.error('Fetch preferences error:', err.message);
		}
	}, [API_URL, user?.id]);

	useEffect(() => {
		fetchNotifications();
		fetchPendingInvites();
		fetchPreferences();
	}, [fetchNotifications, fetchPendingInvites, fetchPreferences]);

	useEffect(() => {
		syncBrowserPushState();
	}, [syncBrowserPushState]);

	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === 'visible' && user?.id) {
				fetchNotifications();
			}
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [fetchNotifications, user?.id]);

	useEffect(() => {
		if (!user?.id || !socketBaseUrl) return;
		let isCancelled = false;
		let socket = null;

		const handleNewNotification = ({ notification }) => {
			if (!notification || notification.userID !== user.id) return;
			setNotifications(prev => {
				if (prev.some(item => item._id === notification._id)) return prev;
				return [notification, ...prev];
			});
		};

		const handleReadNotification = ({ notificationID }) => {
			if (!notificationID) return;
			setNotifications(prev => prev.map(item => (
				item._id === notificationID ? { ...item, read: true } : item
			)));
		};

		const handleAllRead = () => {
			setNotifications(prev => prev.map(item => ({ ...item, read: true })));
		};

		const setupSocket = async () => {
			const authPayload = await resolveSocketAuthPayload({
				getToken,
				testAuthToken: testAuthSession?.token,
				userID: user.id
			});
			if (!authPayload || isCancelled) return;
			socket = io(socketBaseUrl, {
				transports: ['websocket', 'polling'],
				auth: authPayload
			});
			socketRef.current = socket;
			socket.on('notification:new', handleNewNotification);
			socket.on('notification:read', handleReadNotification);
			socket.on('notification:all-read', handleAllRead);
		};

		setupSocket();

		return () => {
			isCancelled = true;
			if (socket) {
				socket.off('notification:new', handleNewNotification);
				socket.off('notification:read', handleReadNotification);
				socket.off('notification:all-read', handleAllRead);
				socket.disconnect();
			}
			socketRef.current = null;
		};
	}, [getToken, socketBaseUrl, testAuthSession?.token, user?.id]);

	const markAsRead = async (notifID) => {
		try {
			await axios.post(`${API_URL}/notifications/mark-read/${notifID}`);
			setNotifications(prev => prev.map(n =>
				n._id === notifID ? { ...n, read: true } : n
			));
		} catch (err) {
			console.error('Mark read error:', err.message);
		}
	};

	const markAllRead = async () => {
		if (!user?.id) return;
		try {
			await axios.post(`${API_URL}/notifications/mark-all-read/${user.id}`);
			setNotifications(prev => prev.map(n => ({ ...n, read: true })));
		} catch (err) {
			console.error('Mark all read error:', err.message);
		}
	};

	const savePreferences = async () => {
		try {
			await axios.put(`${API_URL}/users/${user.id}/preferences`, {
				preferences,
				reminderSettings
			});
		} catch (err) {
			console.error('Save preferences error:', err.message);
		}
	};

	const registerPushToken = async () => {
		if (!pushTokenInput.trim()) return;
		try {
			await axios.post(`${API_URL}/users/${user.id}/push-token`, {
				token: pushTokenInput.trim(),
				platform: 'manual'
			});
			setPushTokenInput('');
		} catch (err) {
			console.error('Register push token error:', err.message);
		}
	};

	const enableBrowserNotifications = async () => {
		if (typeof window === 'undefined' || !('Notification' in window)) {
			return;
		}
		if (!canEnableWebPush) {
			return;
		}
		if (pushEnableBusy) return;

		setPushEnableBusy(true);
		try {
			const permission = await Notification.requestPermission();
			setBrowserPermission(permission);
			if (permission !== 'granted') {
				return;
			}

			const registration = await waitForServiceWorkerRegistration();
			if (!registration) {
				console.error('Service worker is not ready yet for web push.');
				return;
			}
			let subscription = await registration.pushManager.getSubscription();
			if (!subscription) {
				subscription = await registration.pushManager.subscribe({
					userVisibleOnly: true,
					applicationServerKey: base64UrlToUint8Array(WEB_PUSH_PUBLIC_KEY)
				});
			}

			await axios.post(`${API_URL}/users/${user.id}/push-token`, {
				token: JSON.stringify(subscription),
				platform: 'web'
			});
			setHasWebPushSubscription(true);
		} catch (error) {
			console.error('Browser notification permission failed:', error.message);
		} finally {
			setPushEnableBusy(false);
		}
	};

	const disableBrowserNotifications = async () => {
		if (!user?.id || pushEnableBusy) return;
		setPushEnableBusy(true);
		try {
			const subscription = await syncBrowserPushState();
			if (subscription) {
				await subscription.unsubscribe();
				await axios.delete(`${API_URL}/users/${user.id}/push-token`, {
					data: { token: JSON.stringify(subscription) }
				});
			}
			setHasWebPushSubscription(false);
			setBrowserPermission(typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported');
		} catch (error) {
			console.error('Disable browser notifications failed:', error.message);
		} finally {
			setPushEnableBusy(false);
		}
	};

	const handleInviteResponse = async (invite, status) => {
		try {
			await axios.post(`${API_URL}/invites/respond/${invite._id}`, { status });
			if (status === 'accepted') {
				await axios.post(
					`${API_URL}/stryds/join-routine/${invite.routineRef.creatorID}/${invite.routineRef.routineName}`,
					{
						userID: user.id,
						username: user.username,
						profileImageLink: user.profileImageUrl,
						userRoutineXP: 0
					}
				);
				await axios.post(`${API_URL}/stryds/joined-routines/${user.id}`, {
					routineName: invite.routineRef.routineName,
					estimatedTimeMinutes: invite.routineRef.estimatedTimeMinutes || '',
					routineCreator: invite.routineRef.creatorUsername || invite.fromUser.username,
					routineCreatorUserID: invite.routineRef.creatorID
				});
			}
			setPendingInvites(prev => prev.filter(item => item._id !== invite._id));
			fetchNotifications();
		} catch (err) {
			console.error('Invite response error:', err.message);
		}
	};

	const getIcon = (type) => {
		switch (type) {
			case 'follow': return <MdPersonAdd />;
			case 'like': return <MdFavorite />;
			case 'comment': return <MdChatBubble />;
			case 'workout_invite': return <MdFitnessCenter />;
			case 'streak': return <MdLocalFireDepartment />;
			case 'xp_milestone': return <MdEmojiEvents />;
			case 'routine_complete': return <MdCheckCircle />;
			case 'chat_message': return <MdMail />;
			case 'mention': return <MdChatBubble />;
			default: return <MdNotifications />;
		}
	};

	const getIconColor = (type) => {
		switch (type) {
			case 'follow': return '#2196F3';
			case 'like': return '#E91E63';
			case 'comment': return '#FF9800';
			case 'workout_invite': return '#4CAF50';
			case 'streak': return '#FF5722';
			case 'xp_milestone': return '#9C27B0';
			case 'chat_message': return '#00BCD4';
			default: return 'var(--text-secondary)';
		}
	};

	const getTimeAgo = (dateStr) => {
		const diff = Date.now() - new Date(dateStr).getTime();
		const mins = Math.floor(diff / 60000);
		if (mins < 1) return 'now';
		if (mins < 60) return `${mins}m ago`;
		const hrs = Math.floor(mins / 60);
		if (hrs < 24) return `${hrs}h ago`;
		const days = Math.floor(hrs / 24);
		if (days < 7) return `${days}d ago`;
		return `${Math.floor(days / 7)}w ago`;
	};

	const formatNotificationMessage = (message) => {
		if (typeof message !== 'string' || !message.trim()) return '';
		return message.charAt(0).toUpperCase() + message.slice(1);
	};

	const unreadCount = notifications.filter(n => !n.read).length;

	if (!user) {
		return (
			<div className={styles.NotificationsPage}>
				<div className={styles.EmptyState}>
					<MdNotifications className={styles.EmptyIcon} />
					<h3>Sign in to see notifications</h3>
					<Link to="/login" className={styles.LoginLink}>Log In</Link>
				</div>
			</div>
		);
	}

	return (
		<div className={styles.NotificationsPage}>
			<div className={styles.Header}>
				<div className={styles.HeaderLeft}>
					<h1 className={styles.Title}>Notifications</h1>
					{unreadCount > 0 && <span className={styles.UnreadCount}>{unreadCount}</span>}
				</div>
				{browserPermission !== 'unsupported' && (
					<div className={styles.BrowserNotifActions}>
						<button
							className={styles.BrowserNotifBtn}
							onClick={enableBrowserNotifications}
							disabled={pushEnableBusy || !canEnableWebPush}
							aria-label="Enable browser notifications"
						>
							<MdNotificationsActive />
							{pushEnableBusy
								? 'Working...'
								: !WEB_PUSH_PUBLIC_KEY
									? 'Web push not configured'
								: browserPermission === 'granted' && hasWebPushSubscription
									? 'Notifications on'
								: browserPermission === 'denied'
									? 'Blocked'
									: 'Enable notifications'}
						</button>
						{hasWebPushSubscription && (
							<button
								className={`${styles.BrowserNotifBtn} ${styles.BrowserNotifBtnSecondary}`}
								onClick={disableBrowserNotifications}
								disabled={pushEnableBusy}
								aria-label="Disable browser notifications"
							>
								Turn off
							</button>
						)}
					</div>
				)}
				{unreadCount > 0 && (
					<button className={styles.MarkAllBtn} onClick={markAllRead}>
						<MdDoneAll /> Mark all read
					</button>
				)}
			</div>

			{SHOW_NOTIFICATION_SETTINGS && (
				<div className={styles.PreferenceSection}>
					<div className={styles.PreferenceHeader}>Reminder & Notification Settings</div>
					<div className={styles.PreferenceGrid}>
						<label className={styles.PreferenceItem}>
							<input
								type="checkbox"
								checked={preferences.pushNotifications}
								onChange={(e) => setPreferences(prev => ({ ...prev, pushNotifications: e.target.checked }))}
							/>
							<span>Push notifications</span>
						</label>
						<label className={styles.PreferenceItem}>
							<input
								type="checkbox"
								checked={preferences.emailNotifications}
								onChange={(e) => setPreferences(prev => ({ ...prev, emailNotifications: e.target.checked }))}
							/>
							<span>Email notifications</span>
						</label>
						<label className={styles.PreferenceItem}>
							<input
								type="checkbox"
								checked={preferences.workoutReminders}
								onChange={(e) => setPreferences(prev => ({ ...prev, workoutReminders: e.target.checked }))}
							/>
							<span>Workout reminders</span>
						</label>
						<label className={styles.PreferenceItem}>
							<span>Reminder lead time (mins)</span>
							<input
								type="number"
								className={styles.PreferenceNumberInput}
								value={reminderSettings.defaultWorkoutReminderMinutesBefore}
								min={5}
								step={5}
								onChange={(e) => setReminderSettings(prev => ({ ...prev, defaultWorkoutReminderMinutesBefore: Number(e.target.value || 30) }))}
							/>
						</label>
					</div>
					<div className={styles.PushTokenRow}>
						<input
							className={styles.PushTokenInput}
							value={pushTokenInput}
							onChange={(e) => setPushTokenInput(e.target.value)}
							placeholder="Paste push token (Expo/APNS/FCM) for this device"
						/>
						<button className={styles.PushTokenBtn} onClick={registerPushToken}>
							Register token
						</button>
					</div>
					<div className={styles.PushTokenRow}>
						<button className={styles.BrowserNotifBtn} onClick={enableBrowserNotifications}>
							<MdNotificationsActive />
							{browserPermission === 'granted'
								? 'Browser notifications enabled'
								: browserPermission === 'denied'
									? 'Browser notifications blocked'
									: 'Enable browser notifications'}
						</button>
					</div>
					<button className={styles.PreferenceSaveBtn} onClick={savePreferences}>
						Save preferences
					</button>
				</div>
			)}

			{pendingInvites.length > 0 && (
				<div className={styles.InvitesSection}>
					<div className={styles.InvitesHeader}>Workout Invites</div>
					<div className={styles.InviteList}>
						{pendingInvites.map(invite => (
							<div className={styles.InviteCard} key={invite._id}>
								<div className={styles.InviteTop}>
									<div className={styles.InviteMessage}>
										<strong>{formatDisplayName(invite.fromUser?.username)}</strong> invited you to{' '}
										<Link to={`/stryd/${invite.routineRef.creatorID}/${invite.routineRef.routineName}`} className={styles.InviteRoutineLink}>
											{invite.routineRef.routineName}
										</Link>
									</div>
								</div>
								<div className={styles.InviteActions}>
									<button
										className={styles.InviteAcceptBtn}
										onClick={() => handleInviteResponse(invite, 'accepted')}
									>
										Accept
									</button>
									<button
										className={styles.InviteDeclineBtn}
										onClick={() => handleInviteResponse(invite, 'declined')}
									>
										Decline
									</button>
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			{loading ? (
				<span className={styles.LoadingText}>Loading...</span>
			) : notifications.length === 0 ? (
				<div className={styles.EmptyState}>
					<MdNotifications className={styles.EmptyIcon} />
					<h3>All caught up</h3>
					<p>You'll see notifications for likes, comments, follows, and more here.</p>
				</div>
			) : (
				<div className={styles.NotifList}>
					{notifications.map(notif => (
						<div
							key={notif._id}
							className={`${styles.NotifItem} ${!notif.read ? styles.NotifUnread : ''}`}
							onClick={() => !notif.read && markAsRead(notif._id)}
						>
							<div className={styles.NotifIcon} style={{ color: getIconColor(notif.type) }}>
								{getIcon(notif.type)}
							</div>

							<div className={styles.NotifContent}>
								{notif.link ? (
									<Link to={notif.link} className={styles.NotifLink}>
										{notif.fromUser?.profileImageLink && (
											<img src={notif.fromUser.profileImageLink} alt="" className={styles.NotifAvatar} />
										)}
										<div className={styles.NotifTextWrapper}>
											<p className={styles.NotifMessage}>{formatNotificationMessage(notif.message)}</p>
											<span className={styles.NotifTime}>{getTimeAgo(notif.createdAt)}</span>
										</div>
									</Link>
								) : (
									<div className={styles.NotifLink}>
										<div className={styles.NotifTextWrapper}>
											<p className={styles.NotifMessage}>{formatNotificationMessage(notif.message)}</p>
											<span className={styles.NotifTime}>{getTimeAgo(notif.createdAt)}</span>
										</div>
									</div>
								)}
							</div>

							{!notif.read && <div className={styles.UnreadDot} />}
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default Notifications;
